import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '../../Components/Date/Datepicker';
import { useSnackbar } from '../../Context/SnackbarProvider';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COLORS } from '../../Styles/Colors';
import apiClient from '../../Services/ApiClient';

type SortBy = 'location' | 'customer' | 'category';

interface InitialFilters {
  selectedOvs: string;
  columnSearches: Record<string, string>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialFilters: InitialFilters;
}

const PDF_COLUMNS = [
  { key: 'created_at',       label: 'Date Created' },
  { key: 'CustomerName',     label: 'Account' },
  { key: 'location_name',    label: 'Location' },
  { key: 'name',             label: 'Description' },
  { key: 'part_number',      label: 'Part No.' },
  { key: 'serial_number',    label: 'Serial No.' },
  { key: 'barcode',          label: 'Barcode' },
  { key: 'category',         label: 'Category' },
  { key: 'manufacturer',     label: 'Manufacturer' },
  { key: 'cost',             label: 'Cost' },
  { key: 'invoice_number',   label: 'Invoice' },
  { key: 'checked_out',      label: 'Checked Out' },
  { key: 'needs_service',    label: 'Needs Service' },
  { key: 'needs_repair',     label: 'Needs Repair' },
];

function buildFilename(ext: string, startDate: Date | null, endDate: Date | null): string {
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  if (startDate && endDate) {
    return `Assets_Merged_${fmt(startDate)}_to_${fmt(endDate)}.${ext}`;
  }
  return `Assets_Merged_All_Dates.${ext}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatBool(val: any): string {
  if (val == null) return '-';
  // mysql2 bit(1) arrives as Buffer or 0/1
  if (val?.type === 'Buffer' && Array.isArray(val.data)) return val.data[0] === 1 ? 'Yes' : 'No';
  return val === 1 || val === true ? 'Yes' : 'No';
}

function generateCsv(rows: any[]): string {
  const headers = PDF_COLUMNS.map((c) => `"${c.label}"`).join(',');
  const lines = rows.map((row) =>
    PDF_COLUMNS.map((c) => {
      const val = row[c.key];
      if (val == null) return '"-"';
      if (c.key === 'checked_out' || c.key === 'needs_service' || c.key === 'needs_repair') {
        return `"${formatBool(val)}"`;
      }
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(','),
  );
  return [headers, ...lines].join('\n');
}

function generatePdf(rows: any[], startDate: Date | null, endDate: Date | null): jsPDF {
  const doc = new jsPDF('l', 'mm', 'a4');
  const fmt = (d: Date) => d.toLocaleDateString();
  const dateRange = startDate && endDate ? `${fmt(startDate)} – ${fmt(endDate)}` : 'All Dates';

  doc.setFontSize(16);
  doc.text('Assets — Merged Export', 14, 15);
  doc.setFontSize(10);
  doc.text(`Date Range: ${dateRange}`, 14, 22);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

  const headers = PDF_COLUMNS.map((c) => c.label);
  const body = rows.map((row) =>
    PDF_COLUMNS.map((c) => {
      const val = row[c.key];
      if (val == null) return '-';
      if (c.key === 'checked_out' || c.key === 'needs_service' || c.key === 'needs_repair') {
        return formatBool(val);
      }
      return String(val);
    }),
  );

  autoTable(doc, {
    head: [headers],
    body,
    startY: 34,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: [32, 178, 170], textColor: 255, fontStyle: 'bold' },
    margin: { left: 14, right: 14 },
  });

  return doc;
}

export const AssetMergeExportDialog: React.FC<Props> = ({ open, onClose, initialFilters }) => {
  const { showSnackbar } = useSnackbar();

  const [sortBy, setSortBy] = useState<Set<SortBy>>(new Set<SortBy>());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  // Reset dates to null each time the dialog opens (assets page has no date filter)
  useEffect(() => {
    if (open) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [open]);

  const toggleSortBy = (col: SortBy) => {
    setSortBy((prev) => {
      const next = new Set(prev);
      next.has(col) ? next.delete(col) : next.add(col);
      return next;
    });
  };

  const buildQueryParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (sortBy.size > 0) params.set('sortBy', Array.from(sortBy).join(','));
    if (startDate) params.set('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.set('endDate', endDate.toISOString().split('T')[0]);
    if (initialFilters.selectedOvs) params.set('ovid', initialFilters.selectedOvs);

    Object.entries(initialFilters.columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });

    return params;
  };

  const fetchRows = async (): Promise<any[] | null> => {
    const params = buildQueryParams();
    const res = await apiClient(`/api/assets/export?${params.toString()}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      showSnackbar(body.error || 'Export failed. Please try again.', 'error');
      return null;
    }
    return res.json();
  };

  const handleExportCsv = async () => {
    setLoadingCsv(true);
    try {
      const rows = await fetchRows();
      if (!rows) return;
      const csv = generateCsv(rows);
      triggerDownload(
        new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        buildFilename('csv', startDate, endDate),
      );
    } catch {
      showSnackbar('Export failed. Please try again.', 'error');
    } finally {
      setLoadingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    setLoadingPdf(true);
    try {
      const rows = await fetchRows();
      if (!rows) return;
      const doc = generatePdf(rows, startDate, endDate);
      doc.save(buildFilename('pdf', startDate, endDate));
    } catch {
      showSnackbar('Export failed. Please try again.', 'error');
    } finally {
      setLoadingPdf(false);
    }
  };

  const isLoading = loadingPdf || loadingCsv;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Merge &amp; Export</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Merge Reporting By:
        </Typography>
        <FormGroup row sx={{ gap: 1, mb: 2 }}>
          {([
            { col: 'location' as SortBy, label: 'Location' },
            { col: 'customer' as SortBy, label: 'Customer' },
            { col: 'category' as SortBy, label: 'Category' },
          ]).map(({ col, label }) => (
            <FormControlLabel
              key={col}
              control={
                <Checkbox
                  checked={sortBy.has(col)}
                  onChange={() => toggleSortBy(col)}
                  sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Select Date Range:
        </Typography>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'capitalize' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExportPdf}
          disabled={isLoading}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingPdf ? 'Exporting…' : 'Export PDF'}
        </Button>
        <Button
          variant="contained"
          onClick={handleExportCsv}
          disabled={isLoading}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingCsv ? 'Exporting…' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
